import React, { useContext, useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Close,
  Dialog,
  HelpDrawer,
  HelpDrawerToggle,
  TextField,
} from "@cmsgov/ds-medicare-gov";
import { getAutocompleteDrugList } from "../../api";
import { useTranslate, sendTealiumEvent } from "../../helpers";
import AtoZDrugList from "./AtoZDrugList";
import { AutocompleteDrug } from "../../@types";
import { FormattedMessage } from "react-intl-phraseapp";
import { AppContext } from "../../app/store";
import { sendDrugConfirmationTealiumEvent } from "../../helpers/prescriptionHelpers";

interface DrugAutocompleteItem {
  id?: string;
  name: string;
  rxcui: string;
  is_generic: boolean;
  generic: { name: string; rxcui: string } | null;
}

interface AddPrescriptionProps {
  onSelectDrug: (addedDrug: {
    is_generic: boolean;
    rxcui: string;
    name: string;
  }) => void;
  goToPlans: () => void;
  goToPharmacySelection: () => void;
}

const AddPrescription: React.FunctionComponent<AddPrescriptionProps> = props => {
  const t = useTranslate();
  const {
    state: { prescriptions, year },
  } = useContext(AppContext);
  const [drugList, setDrugList] = useState([] as DrugAutocompleteItem[]);
  const [selectedDrug, setSelectedDrug] = useState({} as DrugAutocompleteItem);
  const [showGenericPrompt, setShowGenericPrompt] = useState(false);
  const [noResultsMessage, setNoResultsMessage] = useState("");
  const [showOtcDrawer, setShowOtcDrawer] = useState(false);
  const [addedFromAzList, setAddedFromAzList] = useState(false);
  const [error, setError] = useState("");
  let textFieldRef: HTMLInputElement | null = null;

  const onClearTextField = (): void => {
    if (textFieldRef) {
      textFieldRef.value = "";
    }
  };

  const onDrugSelect = (drug: DrugAutocompleteItem): void => {
    setSelectedDrug(drug || {});
  };

  let throttle: NodeJS.Timeout;
  const throttleInMs = 300;

  const onDrugSelectionInputChange = (input: string): void => {
    // Only perform autocomplete if at least 3 characters of input for meaningful results
    if (input.length < 3) {
      setDrugList([]);
      setNoResultsMessage("");
      return;
    }

    // Cancel last timeout because new input was added to search field
    if (throttle) {
      clearTimeout(throttle);
    }

    // Only send request to API after throttleInMs milliseconds to prevent spamming the server
    throttle = setTimeout(async () => {
      try {
        setError("");
        const list = await getAutocompleteDrugList(input, year);

        if (list) {
          // The autocomplete component requires each item to have a string ID. We cannot make use of the RXCUI value because there can be duplicates.
          const drugsWithIds = list.map((drug, i) => {
            return { ...drug, id: i.toString() };
          });

          if (!drugsWithIds.length) {
            setNoResultsMessage(t("add_drugs.no_results"));
          } else {
            setNoResultsMessage("");
          }

          setDrugList(drugsWithIds);

          // work around for strange VO bug where the first item is not selecteable
          // with control option space
          if (drugsWithIds.length) {
            const firstItem = document.querySelector(
              ".AddPrescription__autocomplete li:first-child"
            );
            firstItem?.setAttribute("aria-selected", "true");
            firstItem?.setAttribute("aria-selected", "false");
          }
        }
      } catch (_e) {
        setError(t("drug_search.error"));
      }
    }, throttleInMs);
  };

  const addDrug = (): void => {
    // istanbul ignore next: tested elsewhere
    sendTealiumEvent("Find Plans - Add Drugs", "Search");
    if (!selectedDrug.is_generic && selectedDrug.generic) {
      setShowGenericPrompt(true);
    } else {
      props.onSelectDrug({
        rxcui: selectedDrug.rxcui,
        is_generic: selectedDrug.is_generic,
        name: selectedDrug.name,
      });
    }
  };

  useEffect(() => {
    if (addedFromAzList && selectedDrug.rxcui) {
      addDrug();
      setAddedFromAzList(false);
    }
  }, [selectedDrug, addedFromAzList]);

  return (
    <div className="AddPrescription">
      {showGenericPrompt && selectedDrug.generic && (
        <Dialog
          onExit={(): void => setShowGenericPrompt(false)}
          headerClassName="AddPrescription__promptHeader"
          heading={t("prescription.generic_prompt_header")}
          actions={[
            <Button
              variation="primary"
              size="big"
              key="add-generic-button"
              className="e2e-add-generic-drug-btn"
              onClick={(): void => {
                // istanbul ignore next: tested elsewhere
                sendTealiumEvent("Find Plans - Add Drugs", "Add Generic");
                if (selectedDrug.generic) {
                  props.onSelectDrug({
                    rxcui: selectedDrug.generic.rxcui,
                    is_generic: true,
                    name: selectedDrug.generic.name,
                  });
                }
              }}
            >
              {t("prescription.add_generic")}
            </Button>,

            <Button
              key="add-brand-button"
              variation="transparent"
              className="ds-c-button--primary-alt e2e-add-brand-name-drug-btn"
              onClick={(): void => {
                // istanbul ignore next: tested elsewhere
                sendTealiumEvent("Find Plans - Add Drugs", "Add Brand");
                props.onSelectDrug({
                  rxcui: selectedDrug.rxcui,
                  is_generic: false,
                  name: selectedDrug.name,
                });
              }}
            >
              {t("prescription.add_brand_instead")}
            </Button>,
          ]}
        >
          <p className="ds-u-margin-top--0 addGeneric__text">
            <FormattedMessage
              id="prescription.generic_available"
              tagName="span"
              values={{
                brand: <strong>{selectedDrug.name}</strong>,
                generic: <strong>{selectedDrug.generic.name}</strong>,
              }}
            />
          </p>
          <p className="addGeneric__text">
            <FormattedMessage
              id="prescription.add_generic_instead"
              tagName="span"
              values={{ generic: <strong>{selectedDrug.generic.name}</strong> }}
            />
          </p>
        </Dialog>
      )}

      {error && (
        <div className="ds-u-margin-bottom--2">
          <Alert variation="error" role="alert">
            {error}
          </Alert>
        </div>
      )}
      <div className="AddPrescription__autocompleteContainer">
        <Autocomplete
          items={drugList}
          className="AddPrescription__autocomplete mct-u-full-width-mobile"
          onInputValueChange={onDrugSelectionInputChange}
          onChange={onDrugSelect}
          data-testid="drug-autocomplete"
          clearSearchButton={false}
          noResultsMessage={noResultsMessage}
          inputRef={(element: HTMLInputElement): void => {
            textFieldRef = element;
          }}
        >
          <TextField
            label={t("drug_search.begin_typing")}
            name="Drug search autocomplete"
            fieldClassName="AddPrescription__autocompleteField e2e-drug-selection-autocomplete"
          />
        </Autocomplete>

        <Button
          variation="primary"
          className="AddPrescription__addDrugButton e2e-add-drug-button mct-u-full-width-mobile"
          data-testid="add-drugs"
          onClick={addDrug}
          disabled={!selectedDrug.rxcui}
        >
          {t("drug_search.add_drug")}
        </Button>
      </div>

      <Button
        aria-label={t("add_drugs.autocomplete_clear_search")}
        className="AddPrescription__clearButton"
        onClick={onClearTextField}
        size="small"
        variation="transparent"
      >
        {t("app.terms.clear_search")}
      </Button>

      <div className="AddPrescription__browseDrugsLink">
        <AtoZDrugList
          onAddDrug={(drug: AutocompleteDrug): void => {
            setSelectedDrug(drug);
            setAddedFromAzList(true);
          }}
        />

        <HelpDrawerToggle
          helpDrawerOpen={showOtcDrawer}
          showDrawer={(): void => {
            // istanbul ignore next: tested elsewhere
            sendTealiumEvent("Help Drawer", "Can't Find Your Drug");
            setShowOtcDrawer(true);
          }}
        >
          {t("drug_search.cant_find_drug")}
        </HelpDrawerToggle>

        {showOtcDrawer && (
          <div className="mct-c-help-drawer">
            <HelpDrawer
              onCloseClick={(): void => setShowOtcDrawer(false)}
              heading={t("drug_search.cant_find_drug")}
              closeButtonText={<Close />}
            >
              <div className="ds-u-font-size--base">
                <p>{t("drug_search.cant_find.trouble_adding")}</p>
                <ul>
                  <li>{t("drug_search.cant_find.check_spelling")}</li>
                  <li>{t("drug_search.cant_find.check_otc")}</li>
                </ul>
              </div>
            </HelpDrawer>
          </div>
        )}
      </div>

      <div className="AddPrescription__actions">
        {prescriptions.length === 0 && (
          <Button
            data-testid="continue-to-plans"
            className="ds-c-button--primary-alt-outline mct-u-full-width-mobile ds-u-margin-right--2 ds-u-sm-margin-bottom--0"
            onClick={(): void => {
              // istanbul ignore next: tested elsewhere
              sendTealiumEvent("Find Plans - Add Drugs", "Continue to Plans");
              props.goToPlans();
            }}
            size="big"
          >
            {t("drug_search.continue_to_plans")}
          </Button>
        )}

        <Button
          onClick={(): void => {
            sendDrugConfirmationTealiumEvent(prescriptions);
            props.goToPharmacySelection();
          }}
          size="big"
          className="ds-c-button--primary e2e-drug-next ds-u-margin-top--2 ds-u-sm-margin-top--0 mct-u-full-width-mobile"
        >
          {t("prescription.button.done_adding_drugs")}
        </Button>
      </div>
    </div>
  );
};

export default AddPrescription;
